import { useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { Button } from "barte-design-system";
import { PencilIcon, UploadIcon } from "@phosphor-icons/react";

import { HeaderPage, CardInfo, Title } from "@shared/components";
import { formatDocument } from "@shared/helpers";
import { useGetCompanyDetails } from "@backoffice/services";
import { ICompanyPlan } from "@shared/types";

import {
    SectionCompanyPlans,
    ModalCompanyPlan,
    ModalCompanyPlanUpload,
} from "./components";
import styles from "./show.module.css";

export const CompanyShow = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const { data, isLoading } = useGetCompanyDetails({ companyId: id });

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCompanyPlanId, setSelectedCompanyPlanId] = useState<
        number | undefined
    >(undefined);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [csvData, setCsvData] = useState<Partial<ICompanyPlan> | null>(null);


    const handleOpenModal = (companyPlanId?: number) => {
        if (companyPlanId) {
            setSelectedCompanyPlanId(companyPlanId);
        }

        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedCompanyPlanId(undefined);
        setCsvData(null);
    };

    const handleOpenUploadModal = () => {
        setIsUploadModalOpen(true);
    };

    const handleCloseUploadModal = () => {
        setIsUploadModalOpen(false);
    };

    const onHandleSubmitCSV = (data: Partial<ICompanyPlan>) => {
        setCsvData(data);
        setIsUploadModalOpen(false);
        setIsModalOpen(true);
    };

    return (
        <div className={styles.container}>
            <Helmet>
                <title>
                    {t("backoffice.companyPageTitle")}
                    {data?.data?.name ? ` - ${data.data.name}` : ""}
                </title>
            </Helmet>

            <HeaderPage
                title={data?.data?.name}
                withBackButton
                isLoading={isLoading}
            />

            <div className={styles.content}>
                <CardInfo
                    title={t("backoffice.pageCompanyShowCardInfoTitle")}
                    info={[
                        {
                            label: t("backoffice.pageCompanyShowLabelSellerId"),
                            value: String(data?.data?.id || "-"),
                        },
                        {
                            label: t("backoffice.pageCompanyShowLabelCnpj"),
                            value: formatDocument(data?.data?.cnpj),
                        },
                    ]}
                    isLoading={isLoading}
                />

                <div className={styles["section"]}>
                    <div className={styles["section-title"]}>
                        <Title variant="h6">{t("backoffice.pageCompanyPlansTitle")}</Title>

                        <div className={styles["section-buttons"]}>
                            <Button
                                variant="outline"
                                iconLeft={<PencilIcon />}
                                onClick={() => handleOpenModal()}
                            >
                                {t("backoffice.pageCompanyPlansButtonAdd")}
                            </Button>

                            <Button iconLeft={<UploadIcon />} onClick={handleOpenUploadModal}>
                                {t("backoffice.pageCompanyPlansButtonImport")}
                            </Button>
                        </div>
                    </div>

                    <SectionCompanyPlans
                        companyId={id!}
                        onSelectCompanyPlan={handleOpenModal}
                    />
                </div>
            </div>

            <ModalCompanyPlan
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                companyPlanId={selectedCompanyPlanId}
                companyId={id}
                initialData={csvData}
            />

            <ModalCompanyPlanUpload
                isOpen={isUploadModalOpen}
                onClose={handleCloseUploadModal}
                companyId={id}
                onHandleSubmit={onHandleSubmitCSV}
            />
        </div>
    );
};
